import { useQuery } from "@tanstack/react-query"
import { CircleAlert, Info } from "lucide-react"

import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount, formatPercent } from "@/lib/format"
import { cn } from "@/lib/utils"

import { fetchPnl } from "@/routes/finance/api"
import { useFinanceFilters } from "@/routes/finance/context"
import { compararOrigenes, nombreCuenta, nombrePais } from "@/routes/finance/mapping"
import { t } from "@/lib/i18n"

const MESES = ["ene", "feb", "mar", "abr", "may", "jun",
               "jul", "ago", "sep", "oct", "nov", "dic"]

/** Cuánto puede diferir una cifra antes de que valga la pena mirarla. */
const RELEVANTE = 1

/**
 * El modelo de gestion contra el libro contable, cuenta por cuenta.
 *
 * ES EL PENDIENTE QUE LA PROPIA HOJA LLEVA ANOTADO («Revenue vs facturado
 * cuadrado», «Chequear P&L por pais y total»), y el que cierra el circulo del
 * producto: hasta aca habia un P&L de gestion y unos libros, y nadie podia ver
 * si decian lo mismo.
 *
 * DIFERIR NO ES ESTAR MAL. La hoja lleva forecast y criterios de gestion; el
 * libro lleva lo asentado, y solo lo asentado. Mientras los comprobantes se
 * carguen con retraso, el libro va a ir por detras SIEMPRE, y eso no es un
 * descuadre: es el calendario. Lo que si importa es una cuenta que el libro
 * tiene y el modelo no —un gasto real que nadie presupuesto—, y por eso la tabla
 * se arma con la UNION de las dos y no con las cuentas de la hoja.
 *
 * Los tres avisos del pie no son decoracion. El P&L del libro deja fuera
 * depreciacion, resultado financiero e impuestos porque el modelo no tiene esas
 * lineas; si no se dijera, el libro se veria mas rentable que la realidad. Lo
 * mismo con los asientos que no se pudieron convertir a dolares: no se cuentan
 * como cero, porque un cero afirma que no hubo movimiento.
 */
export function HojaVsLibroPage() {
  const { activeTenantId } = useAuth()
  const { anio, pais, paisQuery } = useFinanceFilters()

  const hoja = useQuery({
    queryKey: ["finance-pnl", activeTenantId, anio, pais, "HOJA_SIETE_FINANZAS"],
    queryFn: () => fetchPnl(anio, paisQuery, "HOJA_SIETE_FINANZAS"),
    enabled: Boolean(activeTenantId),
  })

  const libro = useQuery({
    queryKey: ["finance-pnl", activeTenantId, anio, pais, "LIBRO"],
    queryFn: () => fetchPnl(anio, paisQuery, "LIBRO"),
    enabled: Boolean(activeTenantId),
  })

  const filas = compararOrigenes(hoja.data, libro.data)
  const cargando = hoja.isLoading || libro.isLoading
  const error = hoja.error ?? libro.error

  const ebitdaHoja = hoja.data?.subtotales.ebitda.total ?? 0
  const ebitdaLibro = libro.data?.subtotales.ebitda.total ?? 0

  const fuera = libro.data?.fuera_del_modelo ?? []
  const sinCambio = Object.entries(libro.data?.monedas_sin_tipo_de_cambio ?? {})
  const arrastrado = libro.data?.tipo_de_cambio_arrastrado ?? []
  // Un aviso por moneda, con sus meses: doce filas de "PEN, tomado de 2026-05"
  // dicen lo mismo que una y se leen peor.
  const arrastrePorMoneda = arrastrado.reduce<Record<string, string[]>>((acc, a) => {
    (acc[a.moneda] ??= []).push(MESES[a.mes - 1])
    return acc
  }, {})

  return (
    <div className="space-y-4">
      <MetricRow className="lg:grid-cols-3">
        <MetricCard label={`EBITDA · Finanzas ${anio}`} value={ebitdaHoja}
                    loading={cargando} />
        <MetricCard label={t("EBITDA · Contabilidad")} value={ebitdaLibro}
                    loading={cargando} />
        <MetricCard
          label={t("Diferencia")}
          value={ebitdaLibro - ebitdaHoja}
          loading={cargando}
          tono={Math.abs(ebitdaLibro - ebitdaHoja) > RELEVANTE ? "alerta" : "neutro"}
        />
      </MetricRow>

      <WorkPanel
        title={`Cuenta por cuenta · ${nombrePais(pais || "CONSOLIDADO")}`}
        meta={
          <span className="text-2xs text-muted-foreground">
            {t("En USD. Los costos, en magnitud: el signo lo pone el subtotal.")}</span>
        }
        footnote={t("Diferir no es estar mal. Finanzas lleva forecast y criterios de gestión;\n                  Contabilidad, solo lo asentado. Mientras los comprobantes se carguen con\n                  retraso, Contabilidad va a ir por detrás — eso es el calendario, no un\n                  descuadre. Lo que sí importa es una cuenta que Contabilidad tiene y\n                  Finanzas no.")}
      >
        <DataState
          loading={cargando}
          error={error}
          empty={!cargando && filas.length === 0}
          emptyMessage={t("Ninguno de los dos orígenes tiene cifras en este año.")}
          emptyHint={t("Cambia el año en la barra, o carga comprobantes para que el libro tenga qué mostrar.")}
          onRetry={() => { void hoja.refetch(); void libro.refetch() }}
          columns={[34, 22, 22, 22]}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Cuenta")}</TableHead>
                <TableHead className="text-right">{t("Finanzas")}</TableHead>
                <TableHead className="text-right">{t("Contabilidad")}</TableHead>
                <TableHead className="text-right">{t("Diferencia")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas.map((f) => {
                const soloLibro = f.hoja === 0 && f.libro !== 0
                return (
                  <TableRow key={f.account}>
                    <TableCell className="font-medium">
                      {nombreCuenta(f.account)}
                      {soloLibro ? (
                        <Badge variant="warning-soft" className="ml-1.5">
                          {t("solo en Contabilidad")}</Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="num text-right">{formatAmount(f.hoja)}</TableCell>
                    <TableCell className="num text-right">{formatAmount(f.libro)}</TableCell>
                    <TableCell
                      className={cn(
                        "num text-right",
                        Math.abs(f.diferencia) > RELEVANTE && "font-medium text-warning",
                      )}
                    >
                      {formatAmount(f.diferencia)}
                      {f.hoja ? (
                        <span className="ml-1 text-2xs text-muted-foreground">
                          {formatPercent(f.diferencia / Math.abs(f.hoja), 0)}
                        </span>
                      ) : null}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </DataState>
      </WorkPanel>

      {/* ------------------------------------- lo que el libro dejo fuera --- */}
      {fuera.length > 0 || sinCambio.length > 0 || arrastrado.length > 0 ? (
        <WorkPanel
          title={t("Qué NO entra en la comparación")}
          meta={
            <span className="flex items-center gap-1 text-2xs text-muted-foreground">
              <Info className="h-3 w-3" />
              {t("Del lado de Contabilidad")}</span>
          }
        >
          <div className="space-y-2 p-2.5 text-xs">
            {fuera.length > 0 ? (
              <div>
                <p className="font-medium">
                  {t("Después del EBITDA: Finanzas no tiene estas líneas")}</p>
                <p className="mt-0.5 text-2xs leading-4 text-muted-foreground">
                  {t("Las pestañas por país de Finanzas llevan solo ingresos, costo de ventas, costo de equipo y gastos generales; los impuestos viven en la caja. Se muestran acá y no se descartan: un P&amp;L que tira la depreciación y los impuestos se ve mejor que la realidad.")}</p>
                <ul className="mt-1 space-y-0.5">
                  {fuera.map((f) => (
                    <li key={f.seccion} className="flex items-baseline gap-2">
                      <Badge variant="outline">{f.seccion}</Badge>
                      <span className="num">{formatAmount(f.total)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {sinCambio.length > 0 ? (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/35 bg-destructive-soft p-2"
              >
                <CircleAlert className="mt-px h-3.5 w-3.5 shrink-0 text-destructive" />
                <div>
                  <p className="font-medium text-destructive">
                    {t("Hay asientos que no se pudieron convertir a dólares")}</p>
                  <p className="mt-0.5 text-2xs leading-4 text-foreground/80">
                    {sinCambio.map(([m, n]) => `${m}: ${n} grupo(s)`).join(" · ")}{t(". No se cuentan como cero —un cero afirma que no hubo movimiento— así que el total de Contabilidad está incompleto por ese importe. Carga los tipos de cambio de esa moneda.")}</p>
                </div>
              </div>
            ) : null}

            {Object.keys(arrastrePorMoneda).length > 0 ? (
              <div>
                <p className="font-medium">{t("Meses convertidos con el cambio de otro mes")}</p>
                <p className="mt-0.5 text-2xs leading-4 text-muted-foreground">
                  {t("La tabla de tipos de cambio va por detrás del calendario. Se arrastra el último conocido, que es una aproximación declarada y no un dato:")}{" "}
                  {Object.entries(arrastrePorMoneda).map(([m, meses]) => (
                    <span key={m} className="mr-2">
                      <strong>{m}</strong> en {meses.join(", ")}
                    </span>
                  ))}
                </p>
              </div>
            ) : null}
          </div>
        </WorkPanel>
      ) : null}
    </div>
  )
}
